import React from "react";
import { useTranslation } from "react-i18next";
import ImageUploader from "@/components/ImageUploader";
import type { ActivityFormPayload } from "@/services/api/activity/types";
import { ACTIVITY_CATEGORY_OPTIONS } from "@/utils/activityCategory";

interface ActivityFormInfoStepProps {
  payload: ActivityFormPayload;
  onChange: <K extends keyof ActivityFormPayload>(key: K, value: ActivityFormPayload[K]) => void;
  /** Only business accounts may price an activity; students get a free one. */
  showFee?: boolean;
}

const ActivityFormInfoStep: React.FC<ActivityFormInfoStepProps> = ({
  payload,
  onChange,
  showFee = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-bold text-gray-900">{t("Upload Event Image (max 10)")}</h2>
        <div className="mt-3">
          <ImageUploader
            value={payload.imageUrls}
            onChange={(urls) => onChange("imageUrls", urls)}
            maxCount={10}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-bold text-gray-900">{t("Title*")}</label>
            <input
              type="text"
              value={payload.title}
              onChange={(e) => onChange("title", e.target.value)}
              className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#ffa217] focus:ring-2 focus:ring-[#fff3e0]"
              placeholder={t("Please Input Event Title")}
            />
          </div>

          <div className={`grid gap-4 ${showFee ? "sm:grid-cols-2" : ""}`}>
            {showFee ? (
              <div>
                <label className="text-sm font-bold text-gray-900">{t("Fee")}</label>
                <input
                  type="text"
                  value={payload.fee}
                  onChange={(e) => onChange("fee", e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#ffa217] focus:ring-2 focus:ring-[#fff3e0]"
                  placeholder="0"
                />
              </div>
            ) : null}
            <div>
              <label className="text-sm font-bold text-gray-900">{t("Max Participants")}</label>
              <input
                type="number"
                value={payload.maxParticipants}
                onChange={(e) => onChange("maxParticipants", e.target.value)}
                className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#ffa217] focus:ring-2 focus:ring-[#fff3e0]"
                placeholder="10"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-bold text-gray-900">{t("Description")}</label>
            <textarea
              value={payload.description}
              onChange={(e) => onChange("description", e.target.value)}
              rows={4}
              className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#ffa217] focus:ring-2 focus:ring-[#fff3e0]"
              placeholder={t("Description (optional)")}
            />
          </div>

          <div>
            <label className="text-sm font-bold text-gray-900">{t("Location")}</label>
            <input
              type="text"
              value={payload.location}
              onChange={(e) => onChange("location", e.target.value)}
              className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#ffa217] focus:ring-2 focus:ring-[#fff3e0]"
              placeholder={t("Location (optional)")}
            />
          </div>

          <div>
            <label className="text-sm font-bold text-gray-900">{t("Meeting Location")}</label>
            <input
              type="text"
              value={payload.meetingLocation}
              onChange={(e) => onChange("meetingLocation", e.target.value)}
              className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#ffa217] focus:ring-2 focus:ring-[#fff3e0]"
              placeholder={t("Location (optional)")}
            />
          </div>

          <div>
            <label className="text-sm font-bold text-gray-900">{t("Category")}</label>
            <select
              value={payload.category}
              onChange={(e) => onChange("category", e.target.value)}
              className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#ffa217] focus:ring-2 focus:ring-[#fff3e0]"
            >
              <option value="">{t("Select a category")}</option>
              {ACTIVITY_CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ActivityFormInfoStep;
